import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import GenericService from "../../services/GenericService";
import { UserService } from "../../services/UserService";
import { isAdmin } from "../../services/AuthService";

interface SystemStats {
  users: {
    total: number;
    active: number;
    admins: number;
  };
  emails: {
    total: number;
    invalid: number;
  };
  system: {
    uptime: string;
    version: string;
    environment: string;
  };
}

interface ServiceStatus {
  name: string;
  status: "healthy" | "warning" | "error";
  response_time: number;
  last_check: string;
}

const SystemOverview: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Check admin permissions
  useEffect(() => {
    if (!isAdmin()) {
      return;
    }
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    if (!isAdmin()) return;

    setLoading(true);
    try {
      // Load various statistics
      const [usersResponse, emailsResponse] = await Promise.allSettled([
        UserService.getUsers({ page: 1, per_page: 1 }),
        GenericService.getEmails({ page: 1, per_page: 1 }),
      ]);

      // Mock data for demonstration - in a real app, these would come from actual API endpoints
      const systemStats: SystemStats = {
        users: {
          total:
            usersResponse.status === "fulfilled"
              ? usersResponse.value.pagination?.total || 0
              : 0,
          active:
            usersResponse.status === "fulfilled"
              ? Math.floor((usersResponse.value.pagination?.total || 0) * 0.8)
              : 0,
          admins:
            usersResponse.status === "fulfilled"
              ? Math.floor((usersResponse.value.pagination?.total || 0) * 0.1)
              : 0,
        },
        emails: {
          total:
            emailsResponse.status === "fulfilled"
              ? emailsResponse.value.pagination?.total || 0
              : 0,
          invalid: 0,
        },
        system: {
          uptime: "15 days, 6 hours",
          version: "1.0.0",
          environment: "production",
        },
      };

      // Mock service statuses
      const serviceStatuses: ServiceStatus[] = [
        {
          name: "Authentication Service",
          status: "healthy",
          response_time: 45,
          last_check: new Date().toISOString(),
        },
        {
          name: "User Management",
          status: "healthy",
          response_time: 62,
          last_check: new Date().toISOString(),
        },
        {
          name: "Generic Services",
          status: "healthy",
          response_time: 38,
          last_check: new Date().toISOString(),
        },
        {
          name: "Database",
          status: "healthy",
          response_time: 12,
          last_check: new Date().toISOString(),
        },
        {
          name: "File Storage",
          status: "warning",
          response_time: 150,
          last_check: new Date().toISOString(),
        },
      ];

      setStats(systemStats);
      setServices(serviceStatuses);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to load system data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "warning":
        return <WarningIcon sx={{ color: "warning.main" }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main" }} />;
      default:
        return <CheckCircleIcon sx={{ color: "grey.500" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "success";
      case "warning":
        return "warning";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  if (!isAdmin()) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied: You don't have administrator privileges.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 4, mb: 4 }}
      data-testid="system-overview"
    >
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <DashboardIcon sx={{ fontSize: 40, color: "#303f9f" }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin?tab=0")}
                sx={{ mr: 2 }}
              >
                Back to Dashboard
              </Button>
              <Typography variant="h4" component="h1">
                System Overview
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastRefresh.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadSystemData}
          disabled={loading}
          sx={{ bgcolor: "#303f9f", "&:hover": { bgcolor: "#283593" } }}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* System Statistics */}
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            System Statistics
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Users Stats */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <PeopleIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                  <Typography variant="h6" color="primary">
                    Users
                  </Typography>
                  <Typography variant="h4">
                    {stats?.users.total || 0}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block">
                        Active
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        {stats?.users.active || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block">
                        Admins
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {stats?.users.admins || 0}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Emails Stats */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <EmailIcon sx={{ fontSize: 40, color: "#f57c00", mb: 1 }} />
                  <Typography variant="h6" color="warning.main">
                    Emails
                  </Typography>
                  <Typography variant="h4">
                    {stats?.emails.total || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* System Health */}
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            System Health
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* System Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    System Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2">Version:</Typography>
                      <Chip
                        label={stats?.system.version || "Unknown"}
                        size="small"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2">Uptime:</Typography>
                      <Typography variant="body2" color="success.main">
                        {stats?.system.uptime || "Unknown"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2">Environment:</Typography>
                      <Typography variant="body2">
                        {stats?.system.environment || "production"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">CPU Usage</Typography>
                        <Typography variant="body2">45%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={45} />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Memory Usage</Typography>
                        <Typography variant="body2">62%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={62} />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">Storage Usage</Typography>
                        <Typography variant="body2">78%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={78}
                        color="warning"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Service Status */}
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Service Status
          </Typography>

          <Card>
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Response Time</TableCell>
                      <TableCell>Last Check</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {services.map((service, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {getStatusIcon(service.status)}
                            {service.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              service.status.charAt(0).toUpperCase() +
                              service.status.slice(1)
                            }
                            color={getStatusColor(service.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <SpeedIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            />
                            {service.response_time}ms
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(service.last_check).toLocaleTimeString()}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Check Service">
                            <IconButton size="small">
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<StorageIcon />}
                  sx={{ p: 2, height: "auto", flexDirection: "column" }}
                >
                  <Typography variant="body1">Database Backup</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create system backup
                  </Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  sx={{ p: 2, height: "auto", flexDirection: "column" }}
                >
                  <Typography variant="body1">Security Audit</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Run security check
                  </Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TrendingUpIcon />}
                  sx={{ p: 2, height: "auto", flexDirection: "column" }}
                >
                  <Typography variant="body1">Generate Report</Typography>
                  <Typography variant="caption" color="text.secondary">
                    System analytics
                  </Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  sx={{ p: 2, height: "auto", flexDirection: "column" }}
                >
                  <Typography variant="body1">Refresh All</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Update all data
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Container>
  );
};

export default SystemOverview;
